import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Checkbox from 'components/shared/Checkbox'
import Icon from 'components/shared/Icon'
import { FamilyCheckboxWrapper } from './style'
import { FAMILY_PACKAGE_REGULATIONS_LINK } from 'constants/links'
import { FormattedMessage } from 'react-intl'
import { formatPrice } from 'helpers/helpers'
import { ProductBundlePresenter } from 'store/api-slice/hooks'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import { useResolutions } from 'store/resolutions'

interface FamilyCheckboxInterface {
    bundleData: ProductBundlePresenter
    handleCheckboxChange: () => void
    isChecked: boolean
    hasPriceHidden?: boolean
}

export const FamilyCheckbox = ({
    bundleData,
    isChecked,
    handleCheckboxChange,
    hasPriceHidden,
}: FamilyCheckboxInterface) => {
    const { isTablet } = useResolutions()
    const {
        isDarkThemeForTickets,
        isBirthdayPromoForTickets,
        isBlackPromoForTickets,
    } = usePromotions()

    const { name, descriptions, unitPrice, dates } = bundleData
    const daysAmount = dates?.length || 0
    const isDarkMode = isDarkThemeForTickets

    return (
        <FamilyCheckboxWrapper
            isChecked={isChecked}
            gap={[16, 18]}
            isDarkMode={isDarkMode}
            isFullWidth
        >
            <Checkbox
                gap={12}
                isChecked={isChecked}
                setIsChecked={handleCheckboxChange}
                testId="package-check-selection"
            >
                <Box
                    direction={['column', 'row']}
                    align={['flex-start', 'center']}
                >
                    <Text
                        variant="heading4"
                        mr={4}
                        {...(isDarkMode ? { color: 'grayscale_0' } : {})}
                    >
                        {name}{' '}
                        {!!unitPrice && !hasPriceHidden && !isTablet && ' - '}
                    </Text>
                    {unitPrice && !hasPriceHidden ? (
                        <Box
                            direction={['column', 'row']}
                            align={['flex-start', 'baseline']}
                            gap={[0, 4]}
                        >
                            <Box direction="row" align="baseline" gap={4}>
                                <Text variant="heading4" color="primary_500">
                                    {formatPrice(unitPrice, true)}
                                </Text>
                                {daysAmount > 1 && (
                                    <Text variant="bodySmall">
                                        (
                                        <FormattedMessage
                                            id="global.plurals.days"
                                            defaultMessage="{days, plural, few {# dni} many {# dni} other {# dnia}}"
                                            values={{
                                                days: daysAmount,
                                            }}
                                        />
                                        )
                                    </Text>
                                )}
                            </Box>

                            {(isBlackPromoForTickets ||
                                isBirthdayPromoForTickets) && (
                                <Text
                                    variant="bodyExtraSmall"
                                    color="grayscale_400"
                                >
                                    (
                                    <FormattedMessage
                                        id="promo.not_combine"
                                        defaultMessage="Nie łączy się z innymi promocjami"
                                    />
                                    )
                                </Text>
                            )}
                        </Box>
                    ) : (
                        <Text variant="body" ml={4}>
                            {' - '}
                            <FormattedMessage
                                id="package.family.choose_date"
                                defaultMessage="wybierz datę przyjazdu, aby zobaczyć cenę"
                            />
                        </Text>
                    )}
                </Box>
            </Checkbox>
            <Box gap={[4, 8]} pl={[32, 36]}>
                {descriptions?.map(({ icon, description }) => (
                    <Box direction="row" align="flex-start" gap={4} key={icon}>
                        <Icon
                            variant={icon ?? 'ticket'}
                            color={isDarkMode ? 'primary_500' : 'secondary_500'}
                        />
                        <Text
                            variant="bodySmall"
                            color={isDarkMode ? 'grayscale_0' : 'grayscale_900'}
                        >
                            {description}
                        </Text>
                    </Box>
                ))}
                {/* TODO: from api data get also key for cms regulations */}
                <Text
                    as="a"
                    href={FAMILY_PACKAGE_REGULATIONS_LINK}
                    target="_blank"
                    variant="bodySmall"
                    color={isDarkMode ? 'primary_500' : 'secondary_500'}
                    isUnderlined
                    pt={[16, 0]}
                    tabIndex={0}
                >
                    <FormattedMessage
                        id="package.family.terms_and_conditions"
                        defaultMessage="Zasady oferty"
                    />
                </Text>
            </Box>
        </FamilyCheckboxWrapper>
    )
}
